import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Grid,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    IconButton,
    Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PublicIcon from '@mui/icons-material/Public';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';
import ShareIcon from '@mui/icons-material/Share';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InfoIcon from '@mui/icons-material/Info';
import { parseLocalDate } from '../../utils/dateHelpers';

interface TripIntelligenceSectionProps {
    destination: string;
    startDate?: string;
    endDate?: string;
    tripTitle: string;
    hasFlights: boolean;
    hasHotels?: boolean;
    hasTransport?: boolean;
    flights?: {
        title?: string;
        location?: string;
        start_date?: string;
        end_date?: string;
        start_time?: string;
        notes?: string;
    }[];
    hotels?: {
        title?: string;
        location?: string;
        start_date?: string;
        end_date?: string;
        start_time?: string;
        notes?: string;
    }[];
    transport?: {
        title?: string;
        location?: string;
        start_date?: string;
        end_date?: string;
        start_time?: string;
        notes?: string;
    }[];
    flightDetails?: {
        airline?: string;
        origin?: string;
        destination?: string;
    }[];
    activeSection?: 'weather' | 'packing' | 'requirements' | 'airline' | 'hotel' | 'transport' | null;
}

// Real weather via Open-Meteo (keyless, CORS-enabled). No fabricated data.
const wmoToType = (code: number): string => {
    if (code === 0) return 'sunny';
    if (code === 1 || code === 2) return 'partly-cloudy';
    if (code === 3 || code === 45 || code === 48) return 'cloudy';
    return 'rainy'; // drizzle / rain / snow / showers / thunderstorm
};

const wxFmtDate = (d: Date): string =>
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const wxIsoDate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + dd;
};

// destination -> lat/lon (also returns country, reused later for Visa)
// Historical "typical for this time of year" via Open-Meteo archive (keyless).
// Averages the same calendar window across the last 7 completed years.
// Real recorded data, honestly labeled as an average (not a forecast).
// Robust geocoder: tries each candidate location string, and for each one
// tries the full string then progressively trimmed variants until Open-Meteo
// resolves. Returns real coordinates or null (caller shows honest "no data").
const geocodeQuery = async (q: string): Promise<{ lat: number; lon: number } | null> => {
    const name = (q || '').trim();
    if (!name) return null;
    const url = 'https://geocoding-api.open-meteo.com/v1/search?name='
        + encodeURIComponent(name) + '&count=1&language=en&format=json';
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const j = await res.json();
        if (j.results && j.results.length > 0) {
            return { lat: j.results[0].latitude, lon: j.results[0].longitude };
        }
    } catch (e) {
        return null;
    }
    return null;
};

// Build trimmed variants of one location string (most specific -> least).
const placeVariants = (raw: string): string[] => {
    const out: string[] = [];
    const push = (v: string) => {
        const t = (v || '').trim();
        if (t && out.indexOf(t) === -1) out.push(t);
    };
    const full = (raw || '').trim();
    push(full);
    if (full.indexOf(',') !== -1) {
        const parts = full.split(',').map(p => p.trim()).filter(Boolean);
        // first part (e.g. street) is rarely geocodable alone; prefer city-ish parts
        for (let i = 1; i < parts.length; i++) push(parts[i]);
        push(parts[0]);
        // pairs like "Reims FR"
        if (parts.length >= 2) push(parts[parts.length - 2] + ' ' + parts[parts.length - 1]);
    }
    // word-trim fallback: "Paris France" -> "Paris"
    const words = full.split(/\s+/).filter(Boolean);
    for (let n = words.length - 1; n >= 1; n--) push(words.slice(0, n).join(' '));
    return out;
};

// Try candidate locations in priority order; first resolved wins.
const geocodePlace = async (candidates: string[]): Promise<{ lat: number; lon: number } | null> => {
    for (const c of candidates) {
        for (const variant of placeVariants(c)) {
            const hit = await geocodeQuery(variant);
            if (hit) return hit;
        }
    }
    return null;
};

const fetchTypicalWeather = async (
    lat: number,
    lon: number,
    start: Date,
    end: Date
): Promise<any | null> => {

    const mmdd = (d: Date) => {
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return '-' + m + '-' + day;
    };
    // Cap the averaged window at the first 10 days of the trip.
    const winEnd = new Date(start);
    winEnd.setDate(winEnd.getDate() + 9);
    const cappedEnd = end.getTime() < winEnd.getTime() ? end : winEnd;
    const startMd = mmdd(start);
    const endMd = mmdd(cappedEnd);

    const thisYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = thisYear - 1; y >= thisYear - 7; y--) years.push(y);

    const calls = years.map((y) => {
        const url = 'https://archive-api.open-meteo.com/v1/archive?latitude=' + lat
            + '&longitude=' + lon
            + '&start_date=' + y + startMd + '&end_date=' + y + endMd
            + '&daily=temperature_2m_max,temperature_2m_min,precipitation_sum'
            + '&temperature_unit=fahrenheit&timezone=auto';
        return fetch(url).then((r) => (r.ok ? r.json() : null));
    });

    const settled = await Promise.allSettled(calls);
    let highSum = 0, lowSum = 0, tempDays = 0;
    let rainDays = 0, precipDays = 0, yearsUsed = 0;
    for (const res of settled) {
        if (res.status !== 'fulfilled' || !res.value || !res.value.daily) continue;
        const dly = res.value.daily;
        const hi = dly.temperature_2m_max || [];
        const lo = dly.temperature_2m_min || [];
        const pr = dly.precipitation_sum || [];
        let counted = false;
        for (let i = 0; i < hi.length; i++) {
            if (hi[i] != null) { highSum += hi[i]; tempDays++; counted = true; }
            if (lo[i] != null) { lowSum += lo[i]; }
            if (pr[i] != null) { precipDays++; if (pr[i] > 1) rainDays++; }
        }
        if (counted) yearsUsed++;
    }
    if (tempDays === 0) return null;

    const rainFrac = precipDays > 0 ? rainDays / precipDays : 0;
    return {
        avgHigh: Math.round(highSum / tempDays),
        avgLow: Math.round(lowSum / tempDays),
        rainFrac,
        yearsUsed,
        monthLabel: start.toLocaleDateString('en-US', { month: 'long' }),
    };
};

const fetchWeatherForecast = async (
    lat: number,
    lon: number,
    start: Date,
    end: Date,
    today: Date,
    maxForecast: Date
): Promise<any[]> => {

    const fetchStart = start.getTime() < today.getTime() ? today : start;
    const fetchEnd = end.getTime() > maxForecast.getTime() ? maxForecast : end;

    const url = 'https://api.open-meteo.com/v1/forecast?latitude=' + lat + '&longitude=' + lon
        + '&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,weather_code'
        + '&temperature_unit=fahrenheit&timezone=auto'
        + '&start_date=' + wxIsoDate(fetchStart) + '&end_date=' + wxIsoDate(fetchEnd);
    const res = await fetch(url);
    if (!res.ok) throw new Error('forecast failed');
    const d = await res.json();

    const out: any[] = [];
    const times = (d.daily && d.daily.time) || [];
    for (let i = 0; i < times.length; i++) {
        const dt = parseLocalDate(times[i]) || new Date(times[i]);
        out.push({
            date: wxFmtDate(dt),
            weather: wmoToType(d.daily.weather_code[i]),
            high: Math.round(d.daily.temperature_2m_max[i]),
            low: Math.round(d.daily.temperature_2m_min[i]),
            precipProb: d.daily.precipitation_probability_max[i],
        });
    }
    return out;
};

// Mock packing suggestions
const generatePackingList = (destination: string, weather: any[]) => {
    const essentials = [
        { item: 'Passport', category: 'Documents', checked: false },
        { item: 'ID / Driver\'s License', category: 'Documents', checked: false },
        { item: 'Travel insurance documents', category: 'Documents', checked: false },
        { item: 'Credit cards / Cash', category: 'Documents', checked: false },
        { item: 'Phone charger', category: 'Electronics', checked: false },
        { item: 'Power adapter (if international)', category: 'Electronics', checked: false },
        { item: 'Medications', category: 'Health', checked: false },
        { item: 'Toiletries', category: 'Personal', checked: false },
    ];

    const hasRain = weather.some(w => w.weather === 'rainy');
    const isCold = weather.some(w => w.low < 50);
    const isHot = weather.some(w => w.high > 80);

    const weatherItems = [];
    if (hasRain) {
        weatherItems.push({ item: 'Umbrella', category: 'Weather', checked: false });
        weatherItems.push({ item: 'Rain jacket', category: 'Weather', checked: false });
    }
    if (isCold) {
        weatherItems.push({ item: 'Warm jacket', category: 'Weather', checked: false });
        weatherItems.push({ item: 'Sweaters / Layers', category: 'Weather', checked: false });
    }
    if (isHot) {
        weatherItems.push({ item: 'Sunscreen', category: 'Weather', checked: false });
        weatherItems.push({ item: 'Sunglasses', category: 'Weather', checked: false });
        weatherItems.push({ item: 'Hat', category: 'Weather', checked: false });
    }

    return [...essentials, ...weatherItems];
};

// Mock visa requirements
// No fabricated visa status. Visa/entry rules depend on the traveler's
// nationality and change over time, so we point users to an authoritative
// search for their destination rather than assert a requirement.
const visaSearchUrl = (destination: string): string =>
    'https://www.google.com/search?q='
    + encodeURIComponent((destination || '').trim() + ' visa requirements entry');

// Mock airline baggage info
// Pull a likely carrier name from the flight item title ("Delta 1234" -> "Delta").
const carrierFromTitle = (title?: string): string => {
    const t = (title || '').trim();
    if (!t) return '';
    // strip a trailing flight number / code (digits, optional leading letters)
    return t.replace(/\s+[A-Z]{0,3}\d{1,5}$/i, '').trim();
};

// Prefilled search for a carrier's official baggage policy (no guessed URLs).
const baggageSearchUrl = (carrier: string): string =>
    'https://www.google.com/search?q='
    + encodeURIComponent((carrier ? carrier + ' ' : '') + 'baggage policy allowance fees');

const TSA_URL = 'https://www.tsa.gov/travel/security-screening';

// General, non-fabricated air-travel tips (no invented dollar fees).
const AIRLINE_GENERAL_TIPS = [
    'Check in online, usually about 24 hours before departure.',
    'Arrive early - many airlines suggest ~2 hours for domestic and ~3 hours for international flights.',
    'Baggage allowances and fees vary by airline and fare - confirm on your carrier\'s site before you fly.',
];

// Mock hotel info
// Format a stored YYYY-MM-DD date for display (reuses parseLocalDate).
const wxDisplayDate = (d?: string): string => {
    if (!d) return '';
    const dt = parseLocalDate(d) || new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Pull the confirmation number out of the normalized notes field ("Confirmation: X").
const extractConfirmation = (notes?: string): string => {
    if (!notes) return '';
    const m = notes.match(/Confirmation:\s*(.+)/i);
    return m ? m[1].trim() : '';
};

// General, non-fabricated hotel tips (apply broadly; NOT specific to this hotel).
const HOTEL_GENERAL_TIPS = [
    'Check-in is usually mid-afternoon and check-out late morning - confirm exact times with your hotel.',
    'Resort, parking, and amenity fees vary by property - confirm any extra charges when booking.',
    'Join the hotel loyalty program for perks like free WiFi.',
    'Verify the cancellation policy before your stay.',
];

// Mock car rental info
// General, non-fabricated car-rental guidance. Insurance/fuel descriptions are
// accurate general education; fees are named as CATEGORIES (no invented amounts).
const CAR_INSURANCE_INFO = [
    'CDW / LDW (Collision / Loss Damage Waiver): covers damage to the rental vehicle.',
    'LIS / SLI (Supplemental Liability): covers third-party damage.',
    'PAI (Personal Accident): often already covered by travel insurance.',
    'PEC (Personal Effects): often already covered by a homeowner/renter policy.',
];
const CAR_GENERAL_TIPS = [
    'Fuel policy matters: full-to-full is usually cheapest; prepaid/fuel-service options often cost more.',
    'Photograph the car (and note existing damage) before driving off and again at return.',
    'Check whether your credit card includes rental car coverage before buying the company\'s.',
    'Decline add-ons you do not need (GPS, prepaid fuel) and refuel before return.',
    'Watch for extra charges - airport surcharge, young/additional-driver, GPS, toll transponder, one-way drop-off. These vary by company and location; confirm your total before booking.',
];

const TripIntelligenceSection: React.FC<TripIntelligenceSectionProps> = ({
    destination,
    startDate,
    endDate,
    tripTitle,
    hasFlights,
    hasHotels = false,
    hasTransport = false,
    flights = [],
    hotels = [],
    transport = [],
    activeSection,
}) => {
    const [expanded, setExpanded] = useState<string | false>('weather');
    const [packingList, setPackingList] = useState<any[]>([]);
    const [weather, setWeather] = useState<any[]>([]);
    const [weatherLoading, setWeatherLoading] = useState(false);
    const [weatherError, setWeatherError] = useState(false);
    const [weatherTooFar, setWeatherTooFar] = useState(false);
    const [typicalWeather, setTypicalWeather] = useState<any | null>(null);

    // React to activeSection prop changes from parent
    useEffect(() => {
        if (activeSection) {
            setExpanded(activeSection);
        }
    }, [activeSection]);

    // Stable key of booking locations so the weather effect re-runs when the
    // actual locations change (not on every parent re-render).
    const bookingLocationsKey = [
        ...hotels.map(h => h.location || ''),
        ...flights.map(f => f.location || ''),
        ...transport.map(t => t.location || ''),
    ].join('|');

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setWeatherError(false);
            setWeatherTooFar(false);
            if (!startDate || !endDate || !destination) {
                setWeather([]);
                setPackingList(generatePackingList(destination, []));
                return;
            }
            const start = parseLocalDate(startDate);
            const end = parseLocalDate(endDate);
            if (!start || !end) {
                setWeather([]);
                setPackingList(generatePackingList(destination, []));
                return;
            }
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const maxForecast = new Date(today);
            maxForecast.setDate(maxForecast.getDate() + 16);
            if (start.getTime() > maxForecast.getTime()) {
                setWeather([]);
                setPackingList(generatePackingList(destination, []));
                setWeatherTooFar(true);
                setTypicalWeather(null);
                try {
                    const geo = await geocodePlace([
                        ...hotels.map(h => h.location || ''),
                        ...flights.map(f => f.location || ''),
                        ...transport.map(t => t.location || ''),
                        destination,
                    ].filter(Boolean));
                    const typ = geo ? await fetchTypicalWeather(geo.lat, geo.lon, start, end) : null;
                    if (!cancelled) setTypicalWeather(typ);
                } catch (e) {
                    if (!cancelled) setTypicalWeather(null);
                }
                return;
            }
            setWeatherLoading(true);
            try {
                const geo = await geocodePlace([
                    ...hotels.map(h => h.location || ''),
                    ...flights.map(f => f.location || ''),
                    ...transport.map(t => t.location || ''),
                    destination,
                ].filter(Boolean));
                const data = geo ? await fetchWeatherForecast(geo.lat, geo.lon, start, end, today, maxForecast) : [];
                if (cancelled) return;
                if (!geo) setWeatherError(true);
                setWeather(data);
                setPackingList(generatePackingList(destination, data));
            } catch (e) {
                if (cancelled) return;
                setWeather([]);
                setPackingList(generatePackingList(destination, []));
                setWeatherError(true);
            } finally {
                if (!cancelled) setWeatherLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [destination, startDate, endDate, bookingLocationsKey]); // eslint-disable-line react-hooks/exhaustive-deps


    const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const togglePackingItem = (index: number) => {
        setPackingList(prev => prev.map((item, i) =>
            i === index ? { ...item, checked: !item.checked } : item
        ));
    };

    const getWeatherIcon = (type: string) => {
        switch (type) {
            case 'sunny': return <WbSunnyIcon sx={{ color: '#FFA726' }} />;
            case 'partly-cloudy': return <CloudIcon sx={{ color: '#78909C' }} />;
            case 'cloudy': return <CloudIcon sx={{ color: '#607D8B' }} />;
            case 'rainy': return <UmbrellaIcon sx={{ color: '#42A5F5' }} />;
            default: return <WbSunnyIcon sx={{ color: '#FFA726' }} />;
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleEmail = () => {
        const subject = encodeURIComponent(`Trip Details: ${tripTitle}`);
        const body = encodeURIComponent(`Check out my trip to ${destination}!\n\nDates: ${startDate} - ${endDate}\n\nView details: ${window.location.href}`);
        window.open(`mailto:?subject=${subject}&body=${body}`);
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Trip to ${destination}`,
                    text: `Check out my trip: ${tripTitle}`,
                    url: window.location.href,
                });
            } catch (err) {
                console.log('Share cancelled');
            }
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert('Link copied to clipboard!');
        }
    };

    const packedCount = packingList.filter(i => i.checked).length;

    return (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent sx={{ p: 0 }}>
                {/* Header with Share/Print Actions */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: '1px solid #eee',
                    bgcolor: '#FAFAFA',
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                        🧳 Trip Intelligence
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Print Trip">
                            <IconButton size="small" onClick={handlePrint}>
                                <PrintIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Email Trip">
                            <IconButton size="small" onClick={handleEmail}>
                                <EmailIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Share Trip">
                            <IconButton size="small" onClick={handleShare}>
                                <ShareIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Weather Forecast */}
                <Accordion
                    expanded={expanded === 'weather'}
                    onChange={handleAccordionChange('weather')}
                    elevation={0}
                    sx={{ '&:before': { display: 'none' } }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ThermostatIcon sx={{ color: '#FF6B35' }} />
                            <Typography sx={{ fontWeight: 600 }}>Weather Forecast</Typography>
                            {weather.length > 0 && (
                                <Chip
                                    label={`${weather[0]?.high}°F`}
                                    size="small"
                                    sx={{ ml: 1, bgcolor: '#FFF3E0', color: '#E65100' }}
                                />
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {weatherLoading ? (
                            <Typography color="text.secondary">Loading forecast...</Typography>
                        ) : weatherError ? (
                            <Typography color="text.secondary">Weather forecast is unavailable right now. Please try again shortly.</Typography>
                        ) : weatherTooFar ? (
                            typicalWeather ? (
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Typical weather for {typicalWeather.monthLabel}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                        <Typography variant="body2">
                                            Highs around <strong>{typicalWeather.avgHigh}°F</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Lows around <strong>{typicalWeather.avgLow}°F</strong>
                                        </Typography>
                                        {typicalWeather.rainFrac > 0.05 && (
                                            <Typography variant="body2" sx={{ color: '#42A5F5' }}>
                                                Rain on roughly {Math.round(typicalWeather.rainFrac * 100)}% of days
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Based on the last several years of recorded weather. Conditions vary year to year. This becomes a live forecast within 16 days of departure — check a local forecast closer to your dates.
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography color="text.secondary">Forecast available closer to your trip (within 16 days of departure).</Typography>
                            )
                        ) : weather.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                                {weather.map((day, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            textAlign: 'center',
                                            minWidth: 80,
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: idx === 0 ? '#E3F2FD' : '#F5F5F5',
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ fontWeight: idx === 0 ? 700 : 400 }}>
                                            {day.date}
                                        </Typography>
                                        <Box sx={{ my: 1 }}>{getWeatherIcon(day.weather)}</Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {day.high}°
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {day.low}°
                                        </Typography>
                                        {day.precipProb != null && day.precipProb > 0 && (
                                            <Typography variant="caption" sx={{ display: 'block', color: '#42A5F5', mt: 0.5 }}>
                                                {day.precipProb}% rain
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography color="text.secondary">Set trip dates to see weather forecast</Typography>
                        )}
                    </AccordionDetails>
                </Accordion>

                <Divider />

                {/* Packing Checklist */}
                <Accordion
                    expanded={expanded === 'packing'}
                    onChange={handleAccordionChange('packing')}
                    elevation={0}
                    sx={{ '&:before': { display: 'none' } }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ChecklistIcon sx={{ color: '#2D6E6A' }} />
                            <Typography sx={{ fontWeight: 600 }}>Packing Checklist</Typography>
                            <Chip
                                label={`${packedCount}/${packingList.length}`}
                                size="small"
                                color={packedCount === packingList.length ? 'success' : 'default'}
                                sx={{ ml: 1 }}
                            />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            {['Documents', 'Electronics', 'Health', 'Personal', 'Weather'].map(category => {
                                const items = packingList.filter(i => i.category === category);
                                if (items.length === 0) return null;
                                return (
                                    <Grid item xs={12} sm={6} key={category}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#666' }}>
                                            {category}
                                        </Typography>
                                        <List dense disablePadding>
                                            {items.map((item, idx) => {
                                                const globalIdx = packingList.findIndex(i => i === item);
                                                return (
                                                    <ListItem
                                                        key={idx}
                                                        disablePadding
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => togglePackingItem(globalIdx)}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                                            <Checkbox
                                                                edge="start"
                                                                checked={item.checked}
                                                                size="small"
                                                                sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={item.item}
                                                            sx={{
                                                                textDecoration: item.checked ? 'line-through' : 'none',
                                                                color: item.checked ? 'text.secondary' : 'text.primary',
                                                            }}
                                                        />
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                <Divider />

                {/* Travel Requirements (Visa) */}
                <Accordion
                    expanded={expanded === 'requirements'}
                    onChange={handleAccordionChange('requirements')}
                    elevation={0}
                    sx={{ '&:before': { display: 'none' } }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PublicIcon sx={{ color: '#4A6FA5' }} />
                            <Typography sx={{ fontWeight: 600 }}>Travel Requirements</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ p: 2, bgcolor: '#F5F5F5', borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                                Entry and visa requirements depend on your nationality and can change. Check the official requirements for {destination || 'your destination'} before you travel.
                            </Typography>
                            <Typography
                                component="a"
                                href={visaSearchUrl(destination)}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                sx={{ fontWeight: 600, color: '#2D6E6A', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                Check visa &amp; entry requirements
                            </Typography>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                <Divider />

                {/* Airport & Airline Info */}
                {hasFlights && (
                    <>
                        <Accordion
                            expanded={expanded === 'airline'}
                            onChange={handleAccordionChange('airline')}
                            elevation={0}
                            sx={{ '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FlightTakeoffIcon sx={{ color: '#2196F3' }} />
                                    <Typography sx={{ fontWeight: 600 }}>Airport &amp; Airline Info</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    {flights.map((f, idx) => (
                                        <Grid item xs={12} key={idx}>
                                            <Box sx={{ p: 2, bgcolor: '#E3F2FD', borderRadius: 2 }}>
                                                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                                    {f.title || 'Flight'}
                                                </Typography>
                                                {f.location && (
                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                        {f.location}
                                                    </Typography>
                                                )}
                                                {(f.start_date || f.start_time) && (
                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                        {wxDisplayDate(f.start_date)}{f.start_time ? ' ' + f.start_time : ''}
                                                    </Typography>
                                                )}
                                                {extractConfirmation(f.notes) && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        Confirmation: {extractConfirmation(f.notes)}
                                                    </Typography>
                                                )}
                                                {carrierFromTitle(f.title) && (
                                                    <Typography
                                                        component="a"
                                                        href={baggageSearchUrl(carrierFromTitle(f.title))}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        variant="body2"
                                                        sx={{ fontWeight: 600, color: '#2D6E6A', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                    >
                                                        Check {carrierFromTitle(f.title)} baggage policy
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    ))}
                                    <Grid item xs={12}>
                                        <Box sx={{ p: 2, bgcolor: '#E8F5E9', borderRadius: 2 }}>
                                            <Typography sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <InfoIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                                                General tips
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                General guidance - confirm specifics with your airline and current TSA rules.
                                            </Typography>
                                            {AIRLINE_GENERAL_TIPS.map((tip, idx) => (
                                                <Typography key={idx} variant="body2">- {tip}</Typography>
                                            ))}
                                            <Typography
                                                component="a"
                                                href={TSA_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="body2"
                                                sx={{ display: 'inline-block', mt: 1, fontWeight: 600, color: '#2D6E6A', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                            >
                                                TSA security &amp; ID requirements
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                        <Divider />
                    </>
                )}

                {/* Hotel Info - NEW SECTION */}
                {hasHotels && (
                    <>
                        <Accordion
                            expanded={expanded === 'hotel'}
                            onChange={handleAccordionChange('hotel')}
                            elevation={0}
                            sx={{ '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <HotelIcon sx={{ color: '#4A6FA5' }} />
                                    <Typography sx={{ fontWeight: 600 }}>Hotel Info</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    {hotels.map((h, idx) => (
                                        <Grid item xs={12} key={idx}>
                                            <Box sx={{ p: 2, bgcolor: '#F3E5F5', borderRadius: 2 }}>
                                                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                                    {h.title || 'Hotel'}
                                                </Typography>
                                                {h.location && (
                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                        {h.location}
                                                    </Typography>
                                                )}
                                                {(h.start_date || h.end_date) && (
                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                        <strong>Check-in:</strong> {wxDisplayDate(h.start_date) || 'TBD'}
                                                        {'  '}<strong>Check-out:</strong> {wxDisplayDate(h.end_date) || 'TBD'}
                                                    </Typography>
                                                )}
                                                {extractConfirmation(h.notes) && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Confirmation: {extractConfirmation(h.notes)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    ))}
                                    <Grid item xs={12}>
                                        <Box sx={{ p: 2, bgcolor: '#E8F5E9', borderRadius: 2 }}>
                                            <Typography sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <InfoIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                                                General tips
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                General guidance - may not apply to your specific hotel.
                                            </Typography>
                                            {HOTEL_GENERAL_TIPS.map((tip, idx) => (
                                                <Typography key={idx} variant="body2">- {tip}</Typography>
                                            ))}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                        <Divider />
                    </>
                )}

                {/* Car/Transport Info - NEW SECTION */}
                {hasTransport && (
                    <Accordion
                        expanded={expanded === 'transport'}
                        onChange={handleAccordionChange('transport')}
                        elevation={0}
                        sx={{ '&:before': { display: 'none' } }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DirectionsCarIcon sx={{ color: '#4A6FA5' }} />
                                <Typography sx={{ fontWeight: 600 }}>Car &amp; Transport Info</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {transport.map((c, idx) => (
                                    <Grid item xs={12} key={idx}>
                                        <Box sx={{ p: 2, bgcolor: '#E3F2FD', borderRadius: 2 }}>
                                            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                                {c.title || 'Car Rental'}
                                            </Typography>
                                            {c.location && (
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    {c.location}
                                                </Typography>
                                            )}
                                            {(c.start_date || c.end_date) && (
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>Pick-up:</strong> {wxDisplayDate(c.start_date) || 'TBD'}
                                                    {'  '}<strong>Drop-off:</strong> {wxDisplayDate(c.end_date) || 'TBD'}
                                                </Typography>
                                            )}
                                            {extractConfirmation(c.notes) && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Confirmation: {extractConfirmation(c.notes)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                ))}
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2, bgcolor: '#E8F5E9', borderRadius: 2 }}>
                                        <Typography sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <InfoIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                                            General tips
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            General guidance - confirm specifics and final pricing with your rental company.
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>Insurance options</Typography>
                                        {CAR_INSURANCE_INFO.map((line, idx) => (
                                            <Typography key={idx} variant="body2">- {line}</Typography>
                                        ))}
                                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>Tips</Typography>
                                        {CAR_GENERAL_TIPS.map((tip, idx) => (
                                            <Typography key={idx} variant="body2">- {tip}</Typography>
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                )}
            </CardContent>
        </Card>
    );
};

export default TripIntelligenceSection;
